import { match, P } from 'ts-pattern';
import {
  Granularity,
  Months,
  TimeSliderGranularity,
} from '../../common/components/timeslider/types';
import {
  endForGranularity,
  format,
  getMomentDate,
  startForGranularity,
} from '../../utils-date';
import { TimePointType } from './types';

export const timePointTypeToString = (value: TimePointType): string => {
  return match(value)
    .with(P.number, (v) => v.toString())
    .with(
      { year: P.number, quarterOfYear: P.number },
      (v) => `${v.year}-${v.quarterOfYear}`
    )
    .with(P.instanceOf(Date), (v) => format(v.valueOf()))
    .exhaustive();
};

export const stringToTimePointType = (
  value: string,
  granularity: TimeSliderGranularity
): TimePointType => {
  return match(granularity)
    .with({ value: Granularity.MONTH }, () => getMomentDate(value).toDate())
    .with({ value: P.union(Granularity.YEAR, Granularity.FINYEAR) }, () =>
      Number(value)
    )
    .with({ value: P.union(Granularity.FINQUARTER) }, () => {
      const [year, quarter] = value.split('-');
      return { year: Number(year), quarterOfYear: Number(quarter) };
    })
    .exhaustive();
};

export const startDateFromTimePointType = (
  value: TimePointType,
  granularity: TimeSliderGranularity,
  firstMonthOfYear: Months
): string => {
  return match({ granularity, value })
    .with(
      { granularity: { value: Granularity.MONTH }, value: P.instanceOf(Date) },
      (v) =>
        format(
          startForGranularity(
            getMomentDate(v.value).valueOf(),
            granularity.value,
            firstMonthOfYear
          )
        )
    )
    .with({ granularity: { value: Granularity.YEAR }, value: P.number }, (v) =>
      format(
        startForGranularity(
          getMomentDate().year(v.value).month(0).date(1).valueOf(),
          granularity.value,
          firstMonthOfYear
        )
      )
    )
    .with(
      { granularity: { value: Granularity.FINYEAR }, value: P.number },
      (v) =>
        format(
          startForGranularity(
            getMomentDate().year(v.value).month(0).date(1).valueOf(),
            granularity.value,
            firstMonthOfYear
          )
        )
    )
    .with(
      {
        granularity: { value: Granularity.FINQUARTER },
        value: { year: P.number, quarterOfYear: P.number },
      },
      (v) => {
        const month = (v.value.quarterOfYear - 1) * 3;
        return format(
          startForGranularity(
            getMomentDate().year(v.value.year).month(month).date(1).valueOf(),
            granularity.value,
            firstMonthOfYear
          )
        );
      }
    )
    .otherwise(() => {
      throw new Error(
        `Invalid time range type and granularity combination: ${value.toString()}, ${granularity.id}`
      );
    });
};

export const endDateFromTimePointType = (
  value: TimePointType,
  granularity: TimeSliderGranularity,
  firstMonthOfYear: Months
): string => {
  const startDate = getMomentDate(
    startDateFromTimePointType(value, granularity, firstMonthOfYear)
  ).valueOf();
  const endDate = endForGranularity(
    getMomentDate(startDate).valueOf(),
    granularity,
    firstMonthOfYear
  );
  return format(endDate);
};
